/**
 * Custom Evaluator Sets API — create and manage custom rubric eval sets.
 *
 * Endpoints used:
 *   GET  /api/evals/custom-evaluator-sets
 *     → Lists eval sets for an organization.
 *
 *   POST /api/evals/custom-evaluator-sets
 *     → Creates a new eval set with an optional initial draft version.
 *
 *   POST /api/evals/custom-evaluator-sets/generate-criteria
 *     → Preview-only: generates a judge rubric from a description.
 *
 *   POST /api/evals/custom-evaluator-sets/{setId}/versions/{versionId}/publish
 *     → Promotes a draft version to active status.
 */
import apiClient, { type ApiResponse } from './client';

// ─── Request types ────────────────────────────────────────────────────────────

export interface CustomEvalCriterionCreate {
  /** Stable, URL-safe identifier for the criterion (max 128 chars). */
  key: string;
  /** Human-readable display name (max 255 chars). */
  name: string;
  /** The evaluation rubric / LLM scoring instructions. */
  rubric: string;
  score_type?: string;
  pass_threshold?: number | null;
  weight?: number;
  order_index?: number;
  is_required?: boolean;
  /** Free-form provenance / generation metadata persisted alongside the criterion. */
  metadata?: Record<string, unknown>;
}

export interface CustomEvalSetVersionCreate {
  notes?: string;
  model_name?: string;
  temperature?: number;
  criteria: CustomEvalCriterionCreate[];
  /**
   * When true and `criteria` is empty, the server seeds the new draft from
   * the set's currently active version. Used by the edit flow to spin up a
   * draft from a published version without re-sending its criteria.
   */
  copy_from_active?: boolean;
  /**
   * Keys to drop when copying from the active version. Ignored unless
   * `copy_from_active` is true.
   */
  remove_keys?: string[];
}

/** Request body for PUT /custom-evaluator-sets/{set_id}/versions/{version_id}. */
export interface CustomEvalSetVersionUpdateRequest {
  notes?: string;
  model_name?: string;
  temperature?: number;
  /** Full replacement list of criteria. Max 10. */
  criteria: CustomEvalCriterionCreate[];
}

export interface CreateCustomEvalSetRequest {
  organization_id: string;
  name: string;
  description?: string;
  user_id?: string;
  initial_version?: CustomEvalSetVersionCreate;
}

export interface PublishVersionRequest {
  organization_id: string;
  user_id?: string;
}

/** Request body for POST /generate-criteria (preview-only, no DB write). */
export interface EvaluatorBuilderRequest {
  organization_id?: string;
  /** Plain-English description of what to evaluate. Min 10 chars (server-validated). */
  description: string;
  /** Optional name or topic hint. Max 255. */
  name_hint?: string;
  /** When true, ground generation in the org's training context if available. Default true. */
  use_training_context?: boolean;
  /** Specific interviewer/training workspace UUID. Defaults to the org primary interviewer. */
  interviewer_id?: string;
  /** Customer goal the evaluator should optimize around. Max 1000. */
  optimization_goal?: string;
  /** Desired visitor/customer outcome this evaluator should measure. Max 1000. */
  desired_outcome?: string;
  /** Target persona or audience segment. Max 1000. */
  target_audience?: string;
}

/**
 * Reserved for a future one-shot create flow. The current builder UI uses
 * generateCriteriaPreview + createEvalSet so user edits to the previewed
 * rubric are preserved.
 */
export interface CriterionGenerateInput {
  description: string;
  name_hint?: string;
}

/**
 * Reserved for a future one-shot create flow. The current builder UI uses
 * generateCriteriaPreview + createEvalSet so user edits to the previewed
 * rubric are preserved.
 */
export interface EvaluatorGenerateAndCreateRequest {
  organization_id: string;
  name?: string;
  description: string;
  additional_criteria?: CriterionGenerateInput[];
  use_training_context?: boolean;
  interviewer_id?: string;
  optimization_goal?: string;
  desired_outcome?: string;
  target_audience?: string;
  user_id?: string;
  model_name?: string;
  temperature?: number;
}

// ─── Response types ───────────────────────────────────────────────────────────

export interface CustomEvalCriterionResponse {
  id: string;
  set_version_id: string;
  key: string;
  name: string;
  rubric: string;
  score_type: string;
  pass_threshold: number | null;
  weight: number;
  order_index: number;
  is_required: boolean;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CustomEvalSetVersionResponse {
  id: string;
  set_id: string;
  version_number: number;
  /** "draft" | "published" | "archived" */
  status: string;
  model_name: string;
  temperature: number;
  notes: string | null;
  published_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  criteria: CustomEvalCriterionResponse[];
}

export interface CustomEvalSetResponse {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  active_version_id: string | null;
  created_at: string;
  updated_at: string;
  active_version: CustomEvalSetVersionResponse | null;
  versions: CustomEvalSetVersionResponse[];
}

export interface CustomEvalSetListResponse {
  sets: CustomEvalSetResponse[];
  total: number;
}

/** Response from POST /generate-criteria. */
export interface EvaluatorBuilderResponse {
  suggested_name: string;
  suggested_slug: string;
  suggested_description: string;
  rubric: string;
  key_checkpoints: string[];
  diagnostic_tags: string[];
}

// ─── Service ──────────────────────────────────────────────────────────────────

const customEvalsService = {
  /**
   * List all custom evaluator sets for an organization.
   */
  listEvalSets: (
    organizationId: string,
    includeVersions: boolean = false,
  ): Promise<ApiResponse<CustomEvalSetListResponse>> =>
    apiClient.get<CustomEvalSetListResponse>('/api/evals/custom-evaluator-sets', {
      organization_id: organizationId,
      include_versions: includeVersions,
    }),

  /**
   * Fetch a single eval set by id, optionally with its full version history.
   */
  getEvalSet: (
    setId: string,
    organizationId: string,
    includeVersions: boolean = true,
  ): Promise<ApiResponse<CustomEvalSetResponse>> =>
    apiClient.get<CustomEvalSetResponse>(
      `/api/evals/custom-evaluator-sets/${setId}`,
      { organization_id: organizationId, include_versions: includeVersions },
    ),

  /**
   * Create a new custom eval set.
   * Pass `initial_version` to also create a draft version with criteria in one call.
   */
  createEvalSet: (
    data: CreateCustomEvalSetRequest,
  ): Promise<ApiResponse<CustomEvalSetResponse>> =>
    apiClient.post<CustomEvalSetResponse>('/api/evals/custom-evaluator-sets', data),

  /**
   * Generate a judge rubric from a plain-English description. Preview only —
   * nothing is persisted. Pair with `createEvalSet({ initial_version })` to save.
   */
  generateCriteriaPreview: (
    data: EvaluatorBuilderRequest,
  ): Promise<ApiResponse<EvaluatorBuilderResponse>> =>
    apiClient.post<EvaluatorBuilderResponse>(
      '/api/evals/custom-evaluator-sets/generate-criteria',
      data,
    ),

  /**
   * Create a new draft version on an existing set. Pass `copy_from_active: true`
   * with empty criteria to seed the draft from the current active version.
   */
  createVersion: (
    setId: string,
    organizationId: string,
    data: CustomEvalSetVersionCreate,
  ): Promise<ApiResponse<CustomEvalSetVersionResponse>> => {
    const url = `/api/evals/custom-evaluator-sets/${setId}/versions?organization_id=${encodeURIComponent(organizationId)}`;
    return apiClient.post<CustomEvalSetVersionResponse>(url, data);
  },

  /**
   * Full-replacement update of a draft version. Backend rejects with 409 if
   * the version's status is anything other than "draft".
   */
  updateVersion: (
    setId: string,
    versionId: string,
    organizationId: string,
    data: CustomEvalSetVersionUpdateRequest,
  ): Promise<ApiResponse<CustomEvalSetVersionResponse>> => {
    const url = `/api/evals/custom-evaluator-sets/${setId}/versions/${versionId}?organization_id=${encodeURIComponent(organizationId)}`;
    return apiClient.put<CustomEvalSetVersionResponse>(url, data);
  },

  /**
   * Delete a draft version. Backend rejects with 409 for non-draft versions.
   */
  deleteVersion: (
    setId: string,
    versionId: string,
    organizationId: string,
  ): Promise<ApiResponse<void>> =>
    apiClient.delete<void>(
      `/api/evals/custom-evaluator-sets/${setId}/versions/${versionId}`,
      { organization_id: organizationId },
    ),

  /**
   * Delete an entire eval set (and all of its versions / criteria).
   */
  deleteEvalSet: (
    setId: string,
    organizationId: string,
  ): Promise<ApiResponse<void>> =>
    apiClient.delete<void>(
      `/api/evals/custom-evaluator-sets/${setId}`,
      { organization_id: organizationId },
    ),

  /**
   * Publish a draft version, making it the active version for the eval set.
   */
  publishVersion: (
    setId: string,
    versionId: string,
    data: PublishVersionRequest,
  ): Promise<ApiResponse<CustomEvalSetResponse>> =>
    apiClient.post<CustomEvalSetResponse>(
      `/api/evals/custom-evaluator-sets/${setId}/versions/${versionId}/publish`,
      data,
    ),
};

export default customEvalsService;
